import React, { useState, useEffect } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import dayjs from 'dayjs';
import isoWeek from 'dayjs/plugin/isoWeek';
import BasePage from './BasePage';
import {
    Typography,
    Card,
    Row,
    Col,
    Statistic,
    Tag,
    Space,
    Button,
    Tabs,
    Table,
    Alert,
    Spin,
    Descriptions,
    message,
    Tooltip as AntTooltip,
    Radio,
    Select,
    DatePicker,
} from 'antd';
import {
    ArrowLeftOutlined,
    ThunderboltOutlined,
    RiseOutlined,
    DollarOutlined,
    SunOutlined,
    CarOutlined,
    WarningOutlined,
} from '@ant-design/icons';
import { LineChart, Line, AreaChart, Area, BarChart, Bar, XAxis, YAxis, CartesianGrid, Tooltip, Legend, ResponsiveContainer, ReferenceLine } from 'recharts';
import { sitesService, equipamentosService, analyticsService } from '../services/api';
import {
    getStatusColor,
    getStatusIcon,
    getStatusText,
    getPlantTypeIcon,
    formatPower,
    formatEnergy,
    formatCurrency,
    getBatteryStatusIcon,
} from '../utils/siteHelpers';

dayjs.extend(isoWeek);

// ── Helpers de fuso horário (America/Sao_Paulo = UTC-3) ──────────────────────
const TZ_BR = 'America/Sao_Paulo';

function toBR(ts) {
    return new Date(new Date(ts).toLocaleString('en-US', { timeZone: TZ_BR }));
}

function getHourBR(ts) {
    return parseInt(
        new Date(ts).toLocaleString('pt-BR', { timeZone: TZ_BR, hour: '2-digit', hour12: false }),
        10
    );
}

function getDateKeyBR(ts) {
    const d = new Date(ts);
    const y = d.toLocaleString('pt-BR', { timeZone: TZ_BR, year: 'numeric' });
    const m = d.toLocaleString('pt-BR', { timeZone: TZ_BR, month: '2-digit' });
    const day = d.toLocaleString('pt-BR', { timeZone: TZ_BR, day: '2-digit' });
    return `${y}-${m}-${day}`;
}

const { Title, Text } = Typography;
const { TabPane } = Tabs;

const SiteDetailsPage = () => {
    const { id } = useParams();
    const navigate = useNavigate();
    
    const [site, setSite] = useState(null);
    const [equipamentos, setEquipamentos] = useState([]);
    const [evChargers, setEvChargers] = useState([]); // EV chargers via analytics (Lambda mock)
    const [loading, setLoading] = useState(true);
    const [chartData, setChartData] = useState([]);
    const [powerCurveData, setPowerCurveData] = useState([]); // leituras brutas para curva de potência
    const [energyData, setEnergyData] = useState([]);          // barras de energia (acumulado)
    const [selectedPeriod, setSelectedPeriod] = useState('day');
    const [periodStats, setPeriodStats] = useState(null);

    // Curva de Potência — seletor independente com DatePicker
    const [powerDate, setPowerDate] = useState(dayjs());       // data de referência para Curva de Potência

    // Monitoramento de Energia — seletor independente
    const [energyPeriod, setEnergyPeriod] = useState('month'); // 'day'|'week'|'month'|'year'
    const [energyDate, setEnergyDate] = useState(dayjs());     // data de referência
    const [inversorRaw, setInversorRaw] = useState([]);        // cache para reprocessar sem re-fetch
    const [gridRaw, setGridRaw] = useState([]);                // cache grid (reprocessar curva de potência)
    const [bateriaRaw, setBateriaRaw] = useState([]);          // cache bateria (reprocessar curva de potência)

    // Seletor de período dos cards KPI (independente dos gráficos)
    const [statsPeriod, setStatsPeriod] = useState('month'); // 'day'|'week'|'month'
    const [statsDate, setStatsDate] = useState(dayjs());     // data de referência dos cards

    // Usinas irmãs (mesmo grupo/empresa) para o seletor de usina
    const [siblingsSites, setSiblingsSites] = useState([]);

    // Sem scroll — gráfico fixo responsivo. 12:00 está naturalmente no centro
    // geométrico do eixo X que sempre cobre 00:00–23:50 (grade fixa de 144 pts).

    const fetchSiteData = async () => {
        try {
            setLoading(true);
            
            // Buscar site com dados em tempo real
            const siteResponse = await sitesService.getWithRealtime(id);
            console.log('🔍 Site response completo:', siteResponse);
            
            if (siteResponse.success) {
                const siteData = siteResponse.data;
                setSite(siteData);

                // Carregar usinas irmãs (mesmo grupo/empresa) para o seletor
                try {
                    const empresaId = siteData?.empresaClienteId || null;
                    const allRes = await sitesService.getAllWithRealtime(empresaId);
                    if (allRes.success && Array.isArray(allRes.data) && allRes.data.length > 1) {
                        setSiblingsSites(allRes.data);
                    } else {
                        setSiblingsSites([]);
                    }
                } catch (_) { setSiblingsSites([]); }
                
                console.log('🔍 ID do site:', id);
                console.log('🔍 Tipo do ID:', typeof id);
                console.log('🔍 powerstation_id:', siteData?.powerstation_id);
                console.log('🔍 realtime existe?', !!siteData?.realtime);
                
                // Verificação simples: se o ID é numérico (1, 2, 3, etc), é site REAL da Lambda
                // Se o ID é UUID, é site MANUAL
                const isNumericId = !isNaN(Number(id)) && Number.isInteger(Number(id));
                
                console.log('🔍 ID é numérico?', isNumericId);
                
                if (isNumericId) {
                    // Site REAL (da Lambda/usinas): buscar equipamentos via API Goodwe
                    console.log('🚀 Site REAL detectado! Buscando equipamentos via /api/usinas/:id/devices');
                    try {
                        const devicesResponse = await equipamentosService.getDevicesFromGoodwe(id);
                        console.log('🚀 Resposta devices Goodwe:', devicesResponse);
                        if (devicesResponse.success && devicesResponse.data) {
                            console.log('✅ Equipamentos encontrados:', devicesResponse.data.length);
                            setEquipamentos(devicesResponse.data);
                        } else {
                            console.log('⚠️ Nenhum equipamento retornado');
                            setEquipamentos([]);
                        }
                    } catch (error) {
                        console.error('❌ Erro ao carregar equipamentos da API Goodwe:', error);
                        setEquipamentos([]);
                    }
                } else {
                    // Site MANUAL (UUID): buscar da tabela equipamentos
                    console.log('📦 Site MANUAL detectado (UUID)! Buscando equipamentos via /api/equipamentos/site/:id');
                    try {
                        const equipResponse = await equipamentosService.getBySite(id);
                        console.log('📦 Resposta equipamentos manual:', equipResponse);
                        if (equipResponse.success) {
                            setEquipamentos(equipResponse.data);
                        } else {
                            setEquipamentos([]);
                        }
                    } catch (error) {
                        console.error('❌ Erro ao buscar equipamentos manuais:', error);
                        setEquipamentos([]);
                    }
                }
                
                // Buscar EV chargers via analytics (Lambda insere em leituras_estacao_recarga)
                // Funciona tanto para sites reais (numeric ID) quanto manuais
                try {
                    const evData = await analyticsService.getEvChargerLatest(id);
                    setEvChargers(Array.isArray(evData?.chargers) ? evData.chargers : []);
                } catch (e) {
                    console.warn('EV charger latest não disponível:', e.message);
                    setEvChargers([]);
                }

                // Buscar dados de gráficos
                await fetchChartData(siteData?.powerstation_id);
            } else {
                // Site não encontrado
                message.error('Site não encontrado. Voltando para o mapa...');
                setTimeout(() => navigate('/mapa-dos-sites'), 2000);
                return;
            }

        } catch (error) {
            console.error('Erro ao buscar dados do site:', error);
            message.error('Site não encontrado ou você não tem permissão. Voltando para o mapa...');
            setTimeout(() => navigate('/mapa-dos-sites'), 2000);
        } finally {
            setLoading(false);
        }
    };

    // Curva de potência multi-série: PV, Rede (signed), Bateria (signed)
    // Para o dia: sempre gera grade de 24h completa (00:00–23:50, intervalo 10 min)
    // garantindo que 12:00 esteja SEMPRE no índice 72 → centralização trivial e confiável.
    const processPowerCurveData = (inversor, period, grid = [], bateria = [], refDate = dayjs()) => {
        if (!inversor || inversor.length === 0) return [];

        // Helper: encontra leitura mais próxima dentro de 15 min
        const findClosest = (sortedTimes, lookup, t) => {
            const W = 15 * 60 * 1000;
            let best = null, bestDiff = Infinity;
            for (const ts of sortedTimes) {
                const diff = Math.abs(ts - t);
                if (diff < bestDiff && diff <= W) { bestDiff = diff; best = ts; }
                if (ts > t + W) break;
            }
            return best != null ? lookup[best] : null;
        };

        if (period === 'day') {
            // Dia de referência em horário BR
            const refDay = refDate.toDate();
            const dayStart = new Date(refDay.getFullYear(), refDay.getMonth(), refDay.getDate(), 0, 0, 0);
            const dayEnd   = new Date(refDay.getFullYear(), refDay.getMonth(), refDay.getDate(), 23, 59, 59);

            // Indexar inversor, grid e bateria do dia por timestamp (ms)
            const invLookup = {};
            inversor.forEach(r => {
                const d = toBR(new Date(r.timestamp));
                if (d >= dayStart && d <= dayEnd) {
                    invLookup[new Date(r.timestamp).getTime()] = r;
                }
            });
            const invTimes = Object.keys(invLookup).map(Number).sort((a, b) => a - b);

            const gridLookup = {};
            (grid || []).forEach(r => { gridLookup[new Date(r.timestamp).getTime()] = r; });
            const gridTimes = Object.keys(gridLookup).map(Number).sort((a, b) => a - b);

            const batLookup = {};
            (bateria || []).forEach(r => { batLookup[new Date(r.timestamp).getTime()] = r; });
            const batTimes = Object.keys(batLookup).map(Number).sort((a, b) => a - b);

            const hasGridData = gridTimes.length > 0;
            const hasBatData  = batTimes.length > 0;

            // Grade fixa de 10 em 10 min → 144 pontos (00:00, 00:10, …, 23:50)
            // 12:00 está SEMPRE no índice 72 → centralização perfeita
            const INTERVAL_MIN = 10;
            const result = [];
            for (let m = 0; m < 1440; m += INTERVAL_MIN) {
                const hh = Math.floor(m / 60);
                const mm = m % 60;
                const hora = `${String(hh).padStart(2,'0')}:${String(mm).padStart(2,'0')}`;

                // Timestamp alvo no dia de referência
                const target = new Date(refDay.getFullYear(), refDay.getMonth(), refDay.getDate(), hh, mm, 0).getTime();

                // Encontra leitura de inversor mais próxima
                const iRec = findClosest(invTimes, invLookup, target);
                const pvKw = iRec ? parseFloat(((iRec.power || 0) / 1000).toFixed(3)) : null;

                let gridKw = undefined;
                if (hasGridData) {
                    const gRec = findClosest(gridTimes, gridLookup, target);
                    if (gRec) {
                        if (gRec.pGridW != null) {
                            // pGridW: positivo = exportando, negativo = importando (convenção GoodWe)
                            // Negamos para: positivo = importando (intuitivo para o usuário)
                            gridKw = parseFloat((-(gRec.pGridW / 1000)).toFixed(3));
                        } else if (gRec.import != null || gRec.export != null) {
                            // Fallback: derivar de import/export separados (W)
                            const imp = gRec.import || 0;   // W, positivo = importando
                            const exp = gRec.export || 0;   // W, positivo = exportando
                            // Net: positivo = importando (mesma convenção do pGridW negado)
                            const net = (imp - exp) / 1000;
                            if (Math.abs(net) > 0.01) gridKw = parseFloat(net.toFixed(3));
                        }
                    }
                }

                let batteryKw = undefined;
                if (hasBatData) {
                    const bRec = findClosest(batTimes, batLookup, target);
                    if (bRec) {
                        if (bRec.pBattery != null) batteryKw = parseFloat(bRec.pBattery.toFixed(3));
                        else if (bRec.pCharge != null || bRec.pDischarge != null)
                            batteryKw = parseFloat(((bRec.pCharge || 0) - (bRec.pDischarge || 0)).toFixed(3));
                    }
                }

                result.push({ hora, potencia: pvKw, gridKw, batteryKw });
            }
            return result;
        }

        // Semana/mês: PV médio por dia (barras diárias sem grid/bateria)
        const daysToShow = period === 'week' ? 7 : 30;
        const refEnd   = new Date(refDate.year(), refDate.month(), refDate.date(), 23, 59, 59);
        const refStart = new Date(refEnd.getTime() - (daysToShow - 1) * 86400000);
        refStart.setHours(0, 0, 0, 0);

        const byDay = {};
        inversor.forEach(r => {
            const d = toBR(new Date(r.timestamp));
            if (d >= refStart && d <= refEnd) {
                const key = getDateKeyBR(r.timestamp);
                if (!byDay[key]) byDay[key] = { sum: 0, count: 0 };
                byDay[key].sum += (r.power || 0) / 1000;
                byDay[key].count++;
            }
        });

        const result = [];
        for (let i = 0; i < daysToShow; i++) {
            const d = new Date(refStart.getTime() + i * 86400000);
            const key = getDateKeyBR(d.toISOString());
            const [, m, day] = key.split('-');
            const avg = byDay[key] ? byDay[key].sum / byDay[key].count : 0;
            result.push({ hora: `${day}/${m}`, potencia: parseFloat(avg.toFixed(3)) });
        }
        return result;
    };

    // Monitoramento de Energia — acumulado por período (sem granulação horária)
    // period: 'day' | 'week' | 'month' | 'year'
    // date: dayjs objeto de referência
    // Fonte: max eday das leituras COM GERAÇÃO (power > 0) por dia.
    // GoodWe não reseta eday à meia-noite — só quando o inversor liga com o sol.
    // Leituras noturnas (power=0) ainda carregam o eday do dia anterior → ignoramos.
    const buildEnergyBars = (inversor, period, date) => {
        if (!inversor || inversor.length === 0) return [];

        // Considera apenas leituras em que o inversor estava gerando (power > 0)
        // para evitar capturar eday residual do dia anterior nas horas noturnas.
        const byDay = {};
        inversor.forEach(r => {
            if ((r.power || 0) <= 0) return; // ignora leituras sem geração
            const key = getDateKeyBR(r.timestamp);
            const gen = r.dailyGeneration || 0;
            if (!byDay[key] || gen > byDay[key]) byDay[key] = gen;
        });

        const fmtLabel = (d) => {
            const [, m, dd] = getDateKeyBR(d.toISOString()).split('-');
            return `${dd}/${m}`;
        };

        if (period === 'day') {
            // Uma única barra com total do dia selecionado
            const key = getDateKeyBR(date.toDate().toISOString());
            return [{ hora: date.format('DD/MM'), energia: parseFloat((byDay[key] || 0).toFixed(2)) }];
        }

        if (period === 'week') {
            // 7 barras: do domingo ao sábado da semana que contém 'date', ou últimos 7 dias
            const result = [];
            for (let i = 6; i >= 0; i--) {
                const d = date.subtract(i, 'day').toDate();
                const key = getDateKeyBR(d.toISOString());
                result.push({ hora: fmtLabel(d), energia: parseFloat((byDay[key] || 0).toFixed(2)) });
            }
            return result;
        }

        if (period === 'month') {
            // Barras diárias para o mês selecionado (do 1º ao último dia)
            const year = date.year();
            const month = date.month(); // 0-based
            const daysInMonth = date.daysInMonth();
            const result = [];
            for (let d = 1; d <= daysInMonth; d++) {
                const dt = new Date(year, month, d);
                const key = getDateKeyBR(dt.toISOString());
                const [, m, dd] = key.split('-');
                result.push({ hora: `${dd}/${m}`, energia: parseFloat((byDay[key] || 0).toFixed(2)) });
            }
            return result;
        }

        if (period === 'year') {
            // 12 barras mensais somando eday de cada mês do ano selecionado
            const year = date.year();
            const byMonth = Array(12).fill(0);
            Object.entries(byDay).forEach(([dayKey, gen]) => {
                const [y, m] = dayKey.split('-').map(Number);
                if (y === year && m >= 1 && m <= 12) byMonth[m - 1] += gen;
            });
            const monthNames = ['Jan','Fev','Mar','Abr','Mai','Jun','Jul','Ago','Set','Out','Nov','Dez'];
            return monthNames.map((name, i) => ({
                hora: name, energia: parseFloat(byMonth[i].toFixed(2))
            }));
        }

        return [];
    };

    const fetchChartData = async (hasPowerstationId) => {
        // Limpa dados imediatamente para evitar flash de dados antigos ao trocar período
        setPowerCurveData([]);
        setEnergyData([]);

        if (hasPowerstationId) {
            // Site REAL: buscar dados reais da API
            // Sempre busca 'month' (30 dias) para cobrir qualquer data selecionada no DatePicker
            // da Curva de Potência. Evita curva zerada em dias históricos quando o site está offline.
            try {
                const readingsResponse = await sitesService.getReadings(id, 'month');
                if (readingsResponse.success) {
                    const { inversor, grid, bateria } = readingsResponse.data;

                    // Cache completo para reprocessar sem re-fetch ao mudar datas
                    setInversorRaw(inversor || []);
                    setGridRaw(grid || []);
                    setBateriaRaw(bateria || []);

                    // Curva de potência: grade fixa 24h para 'day'
                    setPowerCurveData(processPowerCurveData(inversor, selectedPeriod, grid, bateria, powerDate));

                    // Fallback horário (mantém compatibilidade com outros usos)
                    const processedData = processReadingsForChart(inversor, grid, bateria, selectedPeriod);
                    setChartData(processedData);

                    // Monitoramento de Energia — acumulado por período independente
                    setEnergyData(buildEnergyBars(inversor, energyPeriod, energyDate));

                    // Calcular estatísticas do período (usa statsPeriod/statsDate dos cards KPI)
                    const stats = calculatePeriodStats(inversor, grid, bateria, statsPeriod, statsDate);
                    setPeriodStats(stats);
                }
            } catch (error) {
                console.error('Erro ao buscar leituras:', error);
                setPowerCurveData([]);
                setChartData([]);
                setEnergyData([]);
                setInversorRaw([]);
                setGridRaw([]);
                setBateriaRaw([]);
                setPeriodStats(null);
            }
        } else {
            // Site MANUAL: gráficos vazios
            setPowerCurveData([]);
            setChartData([]);
            setEnergyData([]);
            setInversorRaw([]);
            setGridRaw([]);
            setBateriaRaw([]);
            setPeriodStats(null);
        }
    };

    // Calcula estatísticas dos cards KPI usando período calendário (alinhado com SEMS Plus).
    // period: 'day' | 'week' | 'month'
    // date: dayjs – data de referência selecionada pelo usuário
    const calculatePeriodStats = (inversor, _grid, _bateria, period, date) => {
        const refDate = date || dayjs();

        if (!inversor || inversor.length === 0) {
            return { powerNow: 0, generation: 0, totalGeneration: 0, income: 0 };
        }

        // ── Construir range calendário ──────────────────────────────────────
        let startDate, endDate;
        if (period === 'day') {
            // Dia selecionado: 00:00 → 23:59 no horário BR
            startDate = refDate.startOf('day').toDate();
            endDate   = refDate.endOf('day').toDate();
        } else if (period === 'week') {
            // Semana que contém a data (seg → dom)
            startDate = refDate.startOf('isoWeek').toDate();
            endDate   = refDate.endOf('isoWeek').toDate();
        } else {
            // Mês calendário selecionado (01/MM → último dia do mês)
            startDate = refDate.startOf('month').toDate();
            endDate   = refDate.endOf('month').toDate();
        }

        // ── Filtrar leituras do range ───────────────────────────────────────
        const filteredInversor = inversor.filter(r => {
            const ts = new Date(r.timestamp);
            return ts >= startDate && ts <= endDate;
        });

        // Última leitura do array completo para powerNow e totalGeneration
        const lastAll = inversor[inversor.length - 1];
        const powerNow = lastAll?.power || 0;
        const totalGeneration = lastAll?.totalGeneration || 0;

        // ── Calcular geração do período ──────────────────────────────────────
        // GoodWe não reseta eday à meia-noite → só considera leituras com power>0
        let generation = 0;
        if (period === 'day') {
            const gen = filteredInversor.filter(r => (r.power || 0) > 0);
            const last = gen.length > 0 ? gen[gen.length - 1] : null;
            generation = last?.dailyGeneration || 0;
        } else {
            // Semana/Mês: max eday por dia, depois soma (cada dia acumula do zero)
            const dailyGen = {};
            filteredInversor.forEach(r => {
                if ((r.power || 0) <= 0) return;
                const key = getDateKeyBR(r.timestamp);
                const g = r.dailyGeneration || 0;
                if (!dailyGen[key] || g > dailyGen[key]) dailyGen[key] = g;
            });
            generation = Object.values(dailyGen).reduce((s, v) => s + v, 0);
        }

        const tarifa = site?.tarifaKwh || site?.realtime?.tarifaKwh || 0.65;
        return { powerNow, generation, totalGeneration, income: generation * tarifa };
    };

    const processReadingsForChart = (inversor, grid, bateria, period) => {
        // Se não houver dados, retornar array vazio
        if (!inversor || inversor.length === 0) {
            console.log('Nenhuma leitura de inversor encontrada');
            return [];
        }

        console.log(`Processando ${inversor.length} leituras de inversor, período: ${period}`);

        // Agrupar dados por hora/dia dependendo do período
        if (period === 'day') {
            // Para "Hoje": dia completo 00:00–23:00 em horário de Brasília
            // 12:00 sempre no centro do gráfico
            const hourlyData = {};

            // Início e fim do dia atual em BR
            const nowBR = toBR(new Date());
            const todayStart = new Date(nowBR.getFullYear(), nowBR.getMonth(), nowBR.getDate(), 0, 0, 0);
            const todayEnd   = new Date(nowBR.getFullYear(), nowBR.getMonth(), nowBR.getDate(), 23, 59, 59);

            console.log(`Mostrando dia completo: ${todayStart.toLocaleString('pt-BR')} → ${todayEnd.toLocaleString('pt-BR')}`);

            inversor.forEach(reading => {
                const localDate = toBR(reading.timestamp);
                if (localDate >= todayStart && localDate <= todayEnd) {
                    const hour = localDate.getHours();
                    const hourKey = `${hour}`;
                    if (!hourlyData[hourKey]) {
                        hourlyData[hourKey] = { geracao: [], pGridW: [], bateria: [], loadPower: [] };
                    }
                    hourlyData[hourKey].geracao.push(reading.power || 0);
                    if (reading.loadPower != null && reading.loadPower > 0) {
                        hourlyData[hourKey].loadPower.push(reading.loadPower);
                    }
                }
            });

            grid.forEach(reading => {
                const localDate = toBR(reading.timestamp);
                if (localDate >= todayStart && localDate <= todayEnd) {
                    const hour = localDate.getHours();
                    const hourKey = `${hour}`;
                    if (hourlyData[hourKey]) {
                        hourlyData[hourKey].pGridW.push(reading.pGridW ?? 0);
                    }
                }
            });

            bateria.forEach(reading => {
                const localDate = toBR(reading.timestamp);
                if (localDate >= todayStart && localDate <= todayEnd) {
                    const hour = localDate.getHours();
                    const hourKey = `${hour}`;
                    if (hourlyData[hourKey]) {
                        hourlyData[hourKey].bateria.push(reading.soc || 0);
                    }
                }
            });

            console.log('Dados agrupados por hora:', Object.keys(hourlyData).length, 'horas com dados');

            // Array fixo 00:00–23:00 → 12:00 sempre no centro (posição 12/24)
            // Horas sem nenhum dado retornam null → Recharts não renderiza o ponto
            // (ambas as linhas param naturalmente na última leitura real)
            const result = [];
            for (let hour = 0; hour < 24; hour++) {
                const data = hourlyData[`${hour}`];

                // Sem dados para esta hora → null em tudo (linha para aqui)
                if (!data) {
                    result.push({ hora: `${hour}:00`, geracao: null, consumo: null, bateria: null });
                    continue;
                }

                const avgGeracaoW = data.geracao.length > 0
                    ? data.geracao.reduce((a, b) => a + b, 0) / data.geracao.length
                    : 0;

                // Consumo das cargas = Geração + Importação da rede
                // pGridW > 0 → exportando para rede; pGridW < 0 → importando da rede
                // Fórmula: Consumo = Geração - pGridW
                //   (se pGridW = +100 W export  → Consumo = Geração - 100 ✓)
                //   (se pGridW = -100 W import  → Consumo = Geração + 100 ✓)
                let consumoKw = null;
                if (data.pGridW && data.pGridW.length > 0) {
                    const avgPGridW = data.pGridW.reduce((a, b) => a + b, 0) / data.pGridW.length;
                    const computedW = Math.max(0, avgGeracaoW - avgPGridW);
                    consumoKw = parseFloat((computedW / 1000).toFixed(3));
                }

                result.push({
                    hora: `${hour}:00`,
                    geracao: parseFloat((avgGeracaoW / 1000).toFixed(3)),
                    consumo: consumoKw,
                    bateria: data.bateria.length > 0
                        ? data.bateria.reduce((a, b) => a + b, 0) / data.bateria.length
                        : null,
                });
            }

            return result;
        } else {
            // Para week/month, mostrar TODOS os dias do período (com zeros onde não há dados)
            const dailyData = {};
            const daysToShow = period === 'week' ? 7 : 30;
            
            // Encontrar data mais recente nas leituras
            let mostRecentDate = null;
            inversor.forEach(reading => {
                const readingDate = new Date(reading.timestamp);
                if (!mostRecentDate || readingDate > mostRecentDate) {
                    mostRecentDate = readingDate;
                }
            });

            if (!mostRecentDate) {
                return [];
            }

            const recentLocal = toBR(mostRecentDate);
            // Normalizar para início do dia mais recente (00:00:00)
            const endDate = new Date(recentLocal.getFullYear(), recentLocal.getMonth(), recentLocal.getDate(), 23, 59, 59);
            // StartDate: daysToShow-1 dias atrás (para incluir o dia atual)
            const startDate = new Date(endDate.getTime() - (daysToShow - 1) * 24 * 60 * 60 * 1000);
            startDate.setHours(0, 0, 0, 0);
            
            console.log(`Período ${period}: de ${startDate.toISOString()} até ${endDate.toISOString()}`);
            
            // Coletar dados
            inversor.forEach(reading => {
                const localDate = toBR(reading.timestamp);
                
                if (localDate >= startDate && localDate <= endDate) {
                    const dateKey = getDateKeyBR(reading.timestamp);
                    
                    if (!dailyData[dateKey]) {
                        dailyData[dateKey] = { geracao: [], powerW: [], pGridW: [], bateria: [], loadPower: [] };
                    }
                    dailyData[dateKey].geracao.push(reading.dailyGeneration || 0);
                    dailyData[dateKey].powerW.push(reading.power || 0);
                    if (reading.loadPower != null && reading.loadPower > 0) {
                        dailyData[dateKey].loadPower.push(reading.loadPower);
                    }
                }
            });

            grid.forEach(reading => {
                const localDate = toBR(reading.timestamp);
                
                if (localDate >= startDate && localDate <= endDate) {
                    const dateKey = getDateKeyBR(reading.timestamp);
                    
                    if (dailyData[dateKey]) {
                        dailyData[dateKey].pGridW.push(reading.pGridW ?? 0);
                    }
                }
            });

            bateria.forEach(reading => {
                const localDate = toBR(reading.timestamp);
                
                if (localDate >= startDate && localDate <= endDate) {
                    const dateKey = getDateKeyBR(reading.timestamp);
                    
                    if (dailyData[dateKey]) {
                        dailyData[dateKey].bateria.push(reading.soc || 0);
                    }
                }
            });

            // Gerar array com TODOS os dias do período (incluindo dias sem dados)
            const result = [];
            for (let i = 0; i < daysToShow; i++) {
                const date = new Date(startDate.getTime() + i * 24 * 60 * 60 * 1000);
                const dateKey = date.toISOString().split('T')[0];
                const [year, month, day] = dateKey.split('-');
                
                const data = dailyData[dateKey];

                // Consumo diário via balanço energético: Consumo = Geração - pGrid
                // pGridW > 0 → exportando; pGridW < 0 → importando
                let consumoKwh = null;
                if (data && data.powerW.length > 0 && data.pGridW && data.pGridW.length > 0) {
                    const avgPowerW = data.powerW.reduce((a, b) => a + b, 0) / data.powerW.length;
                    const avgPGridW = data.pGridW.reduce((a, b) => a + b, 0) / data.pGridW.length;
                    const avgLoadW = Math.max(0, avgPowerW - avgPGridW);
                    const horasComDados = Math.min(data.powerW.length, 48);
                    consumoKwh = parseFloat(((avgLoadW / 1000) * (horasComDados * 0.5)).toFixed(2));
                }
                // null → Recharts não plota o ponto (evita consumo = geração)

                result.push({
                    hora: `${day}/${month}`,
                    geracao: data && data.geracao.length > 0
                        ? Math.max(...data.geracao) // usar pico do dia (eday no final do dia)
                        : 0,
                    consumo: consumoKwh,
                    bateria: data && data.bateria.length > 0
                        ? data.bateria.reduce((a, b) => a + b, 0) / data.bateria.length
                        : 0,
                });
            }
            
            return result;
        }
    };

    useEffect(() => {
        fetchSiteData();
    }, [id]);

    // Reprocessa curva de potência ao mudar modo (Dia/Semana/Mês) — usa cache
    useEffect(() => {
        if (inversorRaw.length > 0) {
            setPowerCurveData(processPowerCurveData(inversorRaw, selectedPeriod, gridRaw, bateriaRaw, powerDate));
            setChartData(processReadingsForChart(inversorRaw, gridRaw, bateriaRaw, selectedPeriod));
        }
    }, [selectedPeriod]); // eslint-disable-line react-hooks/exhaustive-deps

    // Reprocessa gráfico de energia ao mudar período/data do seletor independente
    useEffect(() => {
        if (inversorRaw.length > 0) {
            setEnergyData(buildEnergyBars(inversorRaw, energyPeriod, energyDate));
        }
    }, [energyPeriod, energyDate]); // eslint-disable-line react-hooks/exhaustive-deps

    // Recalcula stats dos cards KPI ao mudar período/data (sem re-fetch)
    useEffect(() => {
        if (inversorRaw.length > 0) {
            setPeriodStats(calculatePeriodStats(inversorRaw, [], [], statsPeriod, statsDate));
        }
    }, [statsPeriod, statsDate]); // eslint-disable-line react-hooks/exhaustive-deps

    // Reprocessa curva de potência ao mudar data — usa cache (gridRaw, bateriaRaw), sem re-fetch
    useEffect(() => {
        if (inversorRaw.length > 0) {
            setPowerCurveData(processPowerCurveData(inversorRaw, selectedPeriod, gridRaw, bateriaRaw, powerDate));
        }
    }, [powerDate]); // eslint-disable-line react-hooks/exhaustive-deps


    if (loading) {
        return (
            <BasePage>
                <div style={{ textAlign: 'center', padding: '100px 0' }}>
                    <Spin size="large" tip="Carregando dados do site..." />
                </div>
            </BasePage>
        );
    }

    if (!site) {
        return (
            <BasePage>
                <Alert
                    message="Site não encontrado"
                    description="O site solicitado não existe ou você não tem permissão para visualizá-lo."
                    type="error"
                    showIcon
                    action={
                        <Button onClick={() => navigate('/mapa-dos-sites')}>
                            Voltar para Mapa
                        </Button>
                    }
                />
            </BasePage>
        );
    }

    const realtime = site.realtime || {};

    // Mescla equipamentos da Goodwe com EV chargers da Lambda (leituras_estacao_recarga)
    const evAsEquipamento = evChargers.map(ev => ({
        id: ev.id || `ev-${ev.serialNumber}`,
        name: ev.name || `Carregador EV – ${ev.model || 'W-Charge'}`,
        type: 'ESTACAO_RECARGA',
        status: ev.leitura?.status_text === 'CARREGANDO' ? 'CONECTADO'
              : ev.leitura?.status_text === 'CONECTADO'  ? 'CONECTADO'
              : 'CONECTADO',
        capacity: ev.leitura?.p_charge ? (ev.leitura.p_charge / 1000).toFixed(2) : '—',
        manufacturer: ev.manufacturer || 'GoodWe',
        serialNumber: ev.serialNumber || '—',
    }));
    const allEquipamentos = [
        ...equipamentos,
        ...evAsEquipamento.filter(ev =>
            !equipamentos.some(e => e.serialNumber === ev.serialNumber)
        ),
    ];

    // Colunas da tabela de equipamentos
    const equipmentColumns = [
        {
            title: 'Nome',
            dataIndex: 'name',
            key: 'name',
        },
        {
            title: 'Tipo',
            dataIndex: 'type',
            key: 'type',
            render: (type) => {
                const icons = {
                    SOLAR: <SunOutlined style={{ marginRight: 4, color: '#FFB100' }} />,
                    BATERIA: <ThunderboltOutlined style={{ marginRight: 4, color: '#1890ff' }} />,
                    GRID: <ThunderboltOutlined style={{ marginRight: 4, color: '#722ed1' }} />,
                    ESTACAO_RECARGA: <CarOutlined style={{ marginRight: 4, color: '#52c41a' }} />,
                };
                return <span>{icons[type]} {type}</span>;
            },
        },
        {
            title: 'Status',
            dataIndex: 'status',
            key: 'status',
            render: (status) => {
                const colors = {
                    CONECTADO: 'success',
                    DESCONECTADO: 'default',
                    ALERTA: 'warning',
                    FALHA: 'error',
                };
                return <Tag color={colors[status]}>{status}</Tag>;
            },
        },
        {
            title: 'Capacidade',
            dataIndex: 'capacity',
            key: 'capacity',
            render: (capacity, record) => {
                const unit = record.type === 'BATERIA' ? 'kWh' : 'kW';
                return `${capacity} ${unit}`;
            },
        },
        {
            title: 'Fabricante',
            dataIndex: 'manufacturer',
            key: 'manufacturer',
        },
        {
            title: 'Número de Série',
            dataIndex: 'serialNumber',
            key: 'serialNumber',
        },
    ];

    return (
        <BasePage>
            <div style={{ padding: '24px' }}>
                {/* Header */}
                <div style={{ marginBottom: '24px' }}>
                    <Space align="center" style={{ marginBottom: '16px' }}>
                        <Button
                            icon={<ArrowLeftOutlined />}
                            onClick={() => navigate('/mapa-dos-sites')}
                        >
                            Voltar
                        </Button>
                        <span style={{ fontSize: '32px' }}>{getPlantTypeIcon(site.plantType)}</span>
                        <div>
                            {/* Título — vira Select quando há múltiplas usinas no mesmo grupo */}
                            {siblingsSites.length > 1 ? (
                                <Select
                                    value={id}
                                    onChange={(val) => navigate(`/site/${val}`)}
                                    variant="borderless"
                                    style={{
                                        fontSize: 24,
                                        fontWeight: 700,
                                        color: '#262626',
                                        padding: 0,
                                        height: 'auto',
                                        marginBottom: 2,
                                    }}
                                    popupMatchSelectWidth={false}
                                    options={siblingsSites.map(s => ({
                                        value: String(s.id),
                                        label: s.name,
                                    }))}
                                />
                            ) : (
                                <Title level={2} style={{ margin: 0 }}>
                                    {site.name}
                                </Title>
                            )}
                            <Text type="secondary">
                                {site.city}, {site.state} • {site.address}
                            </Text>
                        </div>
                        <AntTooltip
                            title={
                                realtime.plantStatus === 0 && realtime.dailyGeneration > 0
                                    ? 'Conectado. Não gerando no momento (ex.: período sem sol). Gerou hoje.'
                                    : realtime.plantStatus === 1
                                        ? 'Planta gerando energia no momento'
                                        : realtime.plantStatus === -1
                                            ? 'Planta desconectada ou sem comunicação'
                                            : undefined
                            }
                        >
                            <Tag color={getStatusColor(realtime.plantStatus)} style={{ marginLeft: '16px' }}>
                                {getStatusIcon(realtime.plantStatus)} {getStatusText(realtime.plantStatus)}
                            </Tag>
                        </AntTooltip>
                    </Space>

                    <Space style={{ float: 'right' }}>
                        <Button onClick={fetchSiteData} loading={loading}>
                            Atualizar
                        </Button>
                    </Space>
                </div>


                {/* Seletor de período dos cards KPI */}
                <div style={{ display: 'flex', justifyContent: 'flex-end', alignItems: 'center', gap: 8, marginBottom: 12 }}>
                    <DatePicker
                        value={statsDate}
                        onChange={d => d && setStatsDate(d)}
                        picker={statsPeriod === 'month' ? 'month' : 'date'}
                        allowClear={false}
                        size="small"
                        format={statsPeriod === 'month' ? 'MM/YYYY' : 'DD/MM/YYYY'}
                        style={{ width: statsPeriod === 'month' ? 100 : 120 }}
                    />
                    <Radio.Group
                        value={statsPeriod}
                        onChange={e => setStatsPeriod(e.target.value)}
                        optionType="button"
                        buttonStyle="solid"
                        size="small"
                    >
                        <Radio.Button value="day">Dia</Radio.Button>
                        <Radio.Button value="week">Semana</Radio.Button>
                        <Radio.Button value="month">Mês</Radio.Button>
                    </Radio.Group>
                </div>

                {/* Cards de Métricas */}
                <Row gutter={[16, 16]} style={{ marginBottom: '24px' }}>
                    <Col xs={24} sm={12} lg={6}>
                        <Card>
                            <Statistic
                                title="Potência Agora"
                                value={periodStats ? periodStats.powerNow / 1000 : realtime.pvPower / 1000 || 0}
                                precision={2}
                                suffix="kW"
                                prefix={<ThunderboltOutlined />}
                                valueStyle={{ color: '#FB8500' }}
                            />
                        </Card>
                    </Col>
                    <Col xs={24} sm={12} lg={6}>
                        <Card>
                            <Statistic
                                title={
                                    statsPeriod === 'day'
                                        ? `Geração – ${statsDate.format('DD/MM/YYYY')}`
                                        : statsPeriod === 'week'
                                            ? `Geração – Semana de ${statsDate.startOf('isoWeek').format('DD/MM')}`
                                            : `Geração – ${statsDate.format('MM/YYYY')}`
                                }
                                value={periodStats ? periodStats.generation : realtime.dailyGeneration || 0}
                                precision={1}
                                suffix="kWh"
                                prefix={<RiseOutlined />}
                                valueStyle={{ color: '#52c41a' }}
                            />
                        </Card>
                    </Col>
                    <Col xs={24} sm={12} lg={6}>
                        <Card>
                            <Statistic
                                title="Geração Total (Acumulado)"
                                value={periodStats ? periodStats.totalGeneration : realtime.totalGeneration || 0}
                                precision={0}
                                suffix="kWh"
                                prefix={<SunOutlined />}
                            />
                        </Card>
                    </Col>
                    <Col xs={24} sm={12} lg={6}>
                        <Card>
                            <Statistic
                                title={
                                    statsPeriod === 'day'
                                        ? `Receita – ${statsDate.format('DD/MM/YYYY')}`
                                        : statsPeriod === 'week'
                                            ? `Receita – Semana de ${statsDate.startOf('isoWeek').format('DD/MM')}`
                                            : `Receita – ${statsDate.format('MM/YYYY')}`
                                }
                                value={periodStats ? periodStats.income : realtime.dailyIncome || 0}
                                precision={2}
                                prefix={<DollarOutlined />}
                                valueStyle={{ color: '#52c41a' }}
                            />
                        </Card>
                    </Col>
                </Row>

                {/* Linha 1 — Bateria */}
                {(site.batteryCapacity || realtime.batterySoc1 != null || realtime.batterySoc != null) && (() => {
                    const soc    = realtime.batterySoc1 ?? realtime.batterySoc ?? 0;
                    const pwr    = realtime.batteryPower || 0;
                    const batTag = pwr > 0
                        ? { label: 'Descarregando', color: 'orange' }
                        : pwr < 0
                            ? { label: 'Carregando',    color: 'green'  }
                            : { label: 'Standby',       color: 'default' };
                    return (
                        <Alert
                            message={
                                <Space>
                                    <ThunderboltOutlined style={{ color: '#1890ff' }} />
                                    <Text strong>Bateria: {soc}%</Text>
                                    <Tag color={batTag.color} style={{ fontSize: 11, margin: 0 }}>
                                        {batTag.label}
                                    </Tag>
                                    <Text type="secondary" style={{ fontSize: 13 }}>
                                        {Math.abs(pwr / 1000).toFixed(1)} kW
                                    </Text>
                                </Space>
                            }
                            type="info"
                            style={{ marginBottom: evChargers.length > 0 ? '4px' : '24px' }}
                        />
                    );
                })()}

                {/* Linha 2 — EV Charger */}
                {evChargers.length > 0 && (() => {
                    const ev  = evChargers[0];
                    const st  = ev.leitura?.status_text || '';
                    const evTag = st === 'CARREGANDO'
                        ? { label: 'Carregando', color: 'green'   }
                        : st === 'CONECTADO' || st === 'Online' || st === ''
                            ? { label: 'Ocioso',     color: 'default' }
                            : { label: st,           color: 'default' };
                    return (
                        <Alert
                            message={
                                <Space>
                                    <CarOutlined style={{ color: '#FB8500' }} />
                                    <Text strong style={{ color: '#FB8500' }}>
                                        {ev.name || `Carregador EV – ${ev.serialNumber}`}
                                    </Text>
                                    <Tag color={evTag.color} style={{ fontSize: 11, margin: 0 }}>
                                        {evTag.label}
                                    </Tag>
                                    {ev.leitura?.p_charge > 0 && (
                                        <Text type="secondary" style={{ fontSize: 13 }}>
                                            {(ev.leitura.p_charge / 1000).toFixed(2)} kW
                                        </Text>
                                    )}
                                    {ev.leitura?.e_charge_today > 0 && (
                                        <Text type="secondary" style={{ fontSize: 13 }}>
                                            • {ev.leitura.e_charge_today.toFixed(2)} kWh hoje
                                        </Text>
                                    )}
                                    <Text type="secondary" style={{ fontSize: 11 }}>
                                        S/N: {ev.serialNumber}
                                    </Text>
                                </Space>
                            }
                            type="warning"
                            style={{ marginBottom: '24px' }}
                        />
                    );
                })()}

                {/* Tabs */}
                <Tabs defaultActiveKey="1">
                    {/* ── Aba 1: Curva de Potência ───────────────────────────── */}
                    <TabPane tab="Curva de Potência" key="1">
                        <Card
                            title="Curva de Potência"
                            extra={site?.powerstation_id && (
                                <Space size={8}>
                                    <DatePicker
                                        value={powerDate}
                                        onChange={d => d && setPowerDate(d)}
                                        picker={selectedPeriod === 'month' ? 'month' : 'date'}
                                        allowClear={false}
                                        size="small"
                                        format={selectedPeriod === 'month' ? 'MM/YYYY' : 'DD/MM/YYYY'}
                                        style={{ width: selectedPeriod === 'month' ? 100 : 120 }}
                                    />
                                    <Radio.Group
                                        value={selectedPeriod}
                                        onChange={e => setSelectedPeriod(e.target.value)}
                                        optionType="button"
                                        buttonStyle="solid"
                                        size="small"
                                    >
                                        <Radio.Button value="day">Dia</Radio.Button>
                                        <Radio.Button value="week">Semana</Radio.Button>
                                        <Radio.Button value="month">Mês</Radio.Button>
                                    </Radio.Group>
                                </Space>
                            )}
                        >
                            {powerCurveData.length === 0 ? (
                                <div style={{ textAlign: 'center', padding: 60, color: '#999' }}>
                                    Sem dados de potência para o período selecionado.
                                </div>
                            ) : (() => {
                                const isDayView = selectedPeriod === 'day';

                                // Dia: grade fixa 00:00–23:50 → ticks a cada hora (24 labels)
                                // 12:00 fica naturalmente no centro geométrico do eixo X — sem scroll necessário
                                const hourTicksPower = isDayView ? (() => {
                                    const seen = new Set();
                                    return powerCurveData.filter(d => {
                                        const parts = (d.hora || '').split(':');
                                        if (parts[1] !== '00') return false;
                                        const h = parseInt(parts[0]);
                                        if (seen.has(h)) return false;
                                        seen.add(h); return true;
                                    }).map(d => d.hora);
                                })() : undefined;

                                // Se o site tem bateria, mostra rede em ambas as direções (import e export).
                                // Se não tem bateria (ex.: Quarahy), só exibe quando importando (gridKw > 0.05).
                                const siteHasBattery = (site?.batteryCapacity && Number(site.batteryCapacity) > 0) ||
                                    powerCurveData.some(d => d.batteryKw !== undefined && d.batteryKw !== null && Math.abs(d.batteryKw) > 0.05);
                                const hasGrid = isDayView && powerCurveData.some(d =>
                                    d.gridKw !== undefined && d.gridKw !== null &&
                                    (siteHasBattery ? Math.abs(d.gridKw) > 0.05 : d.gridKw > 0.05)
                                );
                                const hasBat  = isDayView && powerCurveData.some(d => d.batteryKw !== undefined && d.batteryKw !== null && Math.abs(d.batteryKw) > 0.05);
                                const yDomain = (hasGrid || hasBat) ? ['auto', 'auto'] : [0, 'auto'];

                                return (
                                    <ResponsiveContainer width="100%" height={420}>
                                        <LineChart
                                            data={powerCurveData}
                                            margin={{ top: 8, right: 24, left: 10, bottom: 8 }}
                                        >
                                            <CartesianGrid strokeDasharray="3 3" stroke="#eee" vertical={false} />
                                            <XAxis
                                                dataKey="hora"
                                                ticks={hourTicksPower}
                                                interval={isDayView ? 0 : 0}
                                                tickLine={false}
                                                tick={({ x, y, payload }) => {
                                                    const h = parseInt((payload.value || '00:00').split(':')[0]);
                                                    const isNoon = isDayView && h === 12;
                                                    return (
                                                        <text x={x} y={y + 14} textAnchor="middle"
                                                            fill={isNoon ? '#fa8c16' : '#888'}
                                                            fontWeight={isNoon ? 700 : 400}
                                                            fontSize={11}>
                                                            {isDayView ? `${String(h).padStart(2,'0')}:00` : payload.value}
                                                        </text>
                                                    );
                                                }}
                                                height={30}
                                            />
                                            <YAxis
                                                tickFormatter={(v) => `${v.toFixed(2)} kW`}
                                                domain={yDomain}
                                                width={75}
                                            />
                                            <Tooltip
                                                formatter={(v, name) => [v != null ? `${Number(v).toFixed(3)} kW` : '—', name]}
                                                labelFormatter={(label) => isDayView ? `Às ${label}` : `Dia ${label}`}
                                            />
                                            <Legend />
                                            {(hasGrid || hasBat) && (
                                                <ReferenceLine y={0} stroke="#ccc" strokeDasharray="4 2" />
                                            )}
                                            <Line type="monotone" dataKey="potencia" stroke="#7B61FF"
                                                strokeWidth={1.5} name="Potência PV"
                                                connectNulls={false} dot={false} activeDot={{ r: 4 }} />
                                            {hasGrid && (
                                                <Line type="monotone" dataKey="gridKw" stroke="#52c41a"
                                                    strokeWidth={1.5} name="Energia da rede"
                                                    connectNulls={false} dot={false} activeDot={{ r: 4 }} />
                                            )}
                                            {hasBat && (
                                                <Line type="monotone" dataKey="batteryKw" stroke="#faad14"
                                                    strokeWidth={1.5} name="Potência da bateria"
                                                    connectNulls={false} dot={false} activeDot={{ r: 4 }} />
                                            )}
                                        </LineChart>
                                    </ResponsiveContainer>
                                );
                            })()}
                        </Card>
                    </TabPane>

                    {/* ── Aba 2: Monitoramento de Energia ───────────────────── */}
                    <TabPane tab="Monitoramento de Energia" key="2">
                        <Card
                            title="Monitoramento de Energia"
                            extra={site?.powerstation_id && (
                                <Space size={8}>
                                    {/* DatePicker adaptado ao modo selecionado */}
                                    <DatePicker
                                        value={energyDate}
                                        onChange={d => d && setEnergyDate(d)}
                                        picker={energyPeriod === 'year' ? 'year' : energyPeriod === 'month' ? 'month' : 'date'}
                                        allowClear={false}
                                        size="small"
                                        format={
                                            energyPeriod === 'year' ? 'YYYY'
                                            : energyPeriod === 'month' ? 'MM/YYYY'
                                            : 'DD/MM/YYYY'
                                        }
                                        style={{ width: energyPeriod === 'year' ? 80 : energyPeriod === 'month' ? 100 : 120 }}
                                    />
                                    <Radio.Group
                                        value={energyPeriod}
                                        onChange={e => setEnergyPeriod(e.target.value)}
                                        optionType="button"
                                        buttonStyle="solid"
                                        size="small"
                                    >
                                        <Radio.Button value="day">Dia</Radio.Button>
                                        <Radio.Button value="week">Semana</Radio.Button>
                                        <Radio.Button value="month">Mês</Radio.Button>
                                        <Radio.Button value="year">Ano</Radio.Button>
                                    </Radio.Group>
                                </Space>
                            )}
                        >
                            {energyData.length === 0 ? (
                                <div style={{ textAlign: 'center', padding: 60, color: '#999' }}>
                                    Sem dados de energia para o período selecionado.
                                </div>
                            ) : (() => {
                                // Energia sempre exibe totais acumulados por período (sem granulação horária)
                                const barLabel = energyPeriod === 'day' ? 'Total do dia (kWh)'
                                    : energyPeriod === 'week' ? 'Geração diária (kWh)'
                                    : energyPeriod === 'month' ? 'Geração diária (kWh)'
                                    : 'Geração mensal (kWh)';

                                const barContent = (
                                    <BarChart
                                        data={energyData}
                                        margin={{ top: 8, right: 24, left: 16, bottom: 8 }}
                                        barCategoryGap="25%"
                                    >
                                        <CartesianGrid strokeDasharray="3 3" vertical={false} stroke="#eee" />
                                        <XAxis
                                            dataKey="hora"
                                            interval={0}
                                            tickLine={false}
                                            tick={({ x, y, payload }) => (
                                                <text x={x} y={y + 14} textAnchor="middle"
                                                    fill="#888" fontSize={energyPeriod === 'month' ? 9 : 11}>
                                                    {payload.value}
                                                </text>
                                            )}
                                            height={30}
                                        />
                                        <YAxis
                                            tickFormatter={(v) => `${v % 1 === 0 ? v : v.toFixed(1)} kWh`}
                                            domain={[0, 'auto']}
                                            width={80}
                                        />
                                        <Tooltip
                                            formatter={(v) => [`${Number(v).toFixed(2)} kWh`, barLabel]}
                                            labelFormatter={(label) => label}
                                        />
                                        <Bar
                                            dataKey="energia"
                                            name={barLabel}
                                            fill="#FFB100"
                                            radius={[3, 3, 0, 0]}
                                            maxBarSize={energyPeriod === 'month' ? 16 : 48}
                                        />
                                    </BarChart>
                                );

                                return (
                                    <ResponsiveContainer width="100%" height={420}>
                                        {barContent}
                                    </ResponsiveContainer>
                                );
                            })()}
                        </Card>
                    </TabPane>

                    <TabPane tab="Equipamentos" key="3">
                        <Card title={`Equipamentos do Site (${allEquipamentos.length})`}>
                            <Table
                                columns={equipmentColumns}
                                dataSource={allEquipamentos}
                                rowKey="id"
                                pagination={false}
                            />
                        </Card>
                    </TabPane>

                    <TabPane tab="Informações" key="4">
                        <Card title="Detalhes do Site">
                            <Descriptions column={2} bordered>
                                <Descriptions.Item label="Nome">{site.name}</Descriptions.Item>
                                <Descriptions.Item label="Tipo">
                                    {getPlantTypeIcon(site.plantType)} {site.plantType}
                                </Descriptions.Item>
                                <Descriptions.Item label="Endereço" span={2}>
                                    {site.address}, {site.city} - {site.state}
                                </Descriptions.Item>
                                <Descriptions.Item label="CEP">{site.zipCode || '-'}</Descriptions.Item>
                                <Descriptions.Item label="Coordenadas">
                                    {site.latitude ? `${site.latitude}, ${site.longitude}` : '-'}
                                </Descriptions.Item>
                                <Descriptions.Item label="Capacidade PV">
                                    {site.pvCapacity ? `${site.pvCapacity} kW` : '-'}
                                </Descriptions.Item>
                                <Descriptions.Item label="Capacidade Bateria">
                                    {site.batteryCapacity ? `${site.batteryCapacity} kWh` : 'Sem bateria'}
                                </Descriptions.Item>
                                <Descriptions.Item label="Goodwe Plant ID" span={2}>
                                    {site.goodwePlantId || 'Não vinculado'}
                                </Descriptions.Item>
                            </Descriptions>
                        </Card>
                    </TabPane>
                </Tabs>
            </div>
        </BasePage>
    );
};

export default SiteDetailsPage;
